import { useEffect, useRef, useState } from 'react';
import { BrowserQRCodeReader } from '@zxing/library';

interface Props {
  onScan: (code: string) => void;
  active: boolean;
}

/**
 * Handles both:
 * 1. Camera-based QR/barcode scanning via @zxing/library
 * 2. USB/Bluetooth keyboard-wedge scanners (rapid keystrokes ending in Enter)
 */
export default function BarcodeScanner({ onScan, active }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const readerRef = useRef<BrowserQRCodeReader | null>(null);
  const [cameraError, setCameraError] = useState<string>('');
  const [cameraActive, setCameraActive] = useState(false);

  // ── Camera scanning ──────────────────────────────────────────────────────
  async function startCamera() {
    try {
      const reader = new BrowserQRCodeReader();
      readerRef.current = reader;
      const devices = await BrowserQRCodeReader.listVideoInputDevices();
      const deviceId = devices[0]?.deviceId;
      if (!deviceId) throw new Error('No camera found');

      await reader.decodeFromVideoDevice(deviceId, videoRef.current!, (result, err) => {
        if (result) {
          onScan(result.getText());
        }
        if (err && err.name !== 'NotFoundException') {
          console.warn('Scanner error:', err);
        }
      });
      setCameraActive(true);
      setCameraError('');
    } catch (e) {
      setCameraError((e as Error).message);
      setCameraActive(false);
    }
  }

  function stopCamera() {
    readerRef.current?.reset();
    setCameraActive(false);
  }

  useEffect(() => {
    if (!active) { stopCamera(); return; }
    return () => stopCamera();
  }, [active]);

  // ── USB keyboard-wedge scanning ──────────────────────────────────────────
  useEffect(() => {
    if (!active) return;

    let buffer = '';
    let lastKeyTime = 0;
    const THRESHOLD_MS = 100; // USB scanners type fast
    const MIN_LENGTH = 4;

    function handleKeyDown(e: KeyboardEvent) {
      const now = Date.now();
      // If gap is too large, start a new buffer
      if (now - lastKeyTime > 500) buffer = '';
      lastKeyTime = now;

      if (e.key === 'Enter') {
        if (buffer.length >= MIN_LENGTH && (now - lastKeyTime) < THRESHOLD_MS * buffer.length) {
          onScan(buffer.trim());
        }
        buffer = '';
        return;
      }

      if (e.key.length === 1) {
        buffer += e.key;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [active, onScan]);

  if (!active) return null;

  return (
    <div className="space-y-4">
      <div className="relative bg-black rounded-lg overflow-hidden aspect-video max-w-sm mx-auto">
        <video ref={videoRef} className="w-full h-full object-cover" />
        {!cameraActive && !cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <button className="btn-primary" onClick={startCamera}>
              Start Camera
            </button>
            <p className="text-xs text-gray-400">or use a USB scanner</p>
          </div>
        )}
        {cameraActive && (
          <>
            {/* Scan overlay */}
            <div className="absolute inset-0 border-2 border-brand-400 rounded-lg pointer-events-none opacity-60" />
            <button
              className="absolute bottom-2 right-2 btn-secondary text-xs"
              onClick={stopCamera}
            >
              Stop
            </button>
          </>
        )}
      </div>
      {cameraError && (
        <p className="text-sm text-red-600 text-center">{cameraError}</p>
      )}
      <p className="text-xs text-gray-500 text-center">
        USB/Bluetooth scanners work automatically — just scan!
      </p>
    </div>
  );
}
